/**
 * Region visual-version polling hook (Macroblock #17, Phase B).
 *
 * Polls `GET /api/v1/snapshot/region` for the current viewport while content is
 * discoverable (intermediate/detail bands) and reports, per tick, the precise set
 * of territory identity_ids whose public visual content changed — the viewer uses
 * this to invalidate ONLY those territories (publication / artwork / brand), never
 * a global bust. Independent of, and complementary to, the chain-block parcel
 * refresh (incl. the Purchase Indexed → Grid Auto-Refresh hotfix), which it never
 * touches.
 *
 * Safety: monotonic request sequence + AbortController drop stale responses; the
 * poll is interval-driven (no self-triggering render loop); disabled state clears
 * cleanly so re-enabling re-baselines without emitting a false invalidation.
 */
import { useEffect, useRef, useState } from "react";

import { makeSnapshotApi, type SnapshotApi, type SnapshotViewport } from "./snapshot-api";
import {
  diffHasChanges,
  diffSnapshots,
  type RegionSnapshot,
} from "./snapshot-version";

export interface UseSnapshotVersionArgs {
  viewport: SnapshotViewport | null;
  enabled: boolean;
  intervalMs?: number;
  api?: SnapshotApi;
}

export interface SnapshotVersionState {
  snapshot: RegionSnapshot | null;
  regionVersion: string | null;
  /** identity_ids to invalidate this epoch (changed ∪ added ∪ removed). */
  changedIdentityIds: string[];
  /** Monotonic; bumps once per tick that carries actionable invalidation. */
  invalidationEpoch: number;
}

const DEFAULT_INTERVAL_MS = 15_000;

export function useSnapshotVersion(args: UseSnapshotVersionArgs): SnapshotVersionState {
  const { viewport, enabled, intervalMs = DEFAULT_INTERVAL_MS } = args;
  const apiRef = useRef<SnapshotApi>(args.api ?? makeSnapshotApi());

  const prevSnapRef = useRef<RegionSnapshot | null>(null);
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<SnapshotVersionState>({
    snapshot: null,
    regionVersion: null,
    changedIdentityIds: [],
    invalidationEpoch: 0,
  });

  // Serialize viewport into a stable dependency key.
  const vpKey = viewport ? `${viewport.x}:${viewport.y}:${viewport.w}:${viewport.h}` : null;

  useEffect(() => {
    if (!enabled || !viewport) {
      // Clear baseline so re-enabling does not emit a spurious diff.
      prevSnapRef.current = null;
      abortRef.current?.abort();
      return;
    }

    let cancelled = false;

    const poll = async () => {
      const mySeq = ++seqRef.current;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      let next: RegionSnapshot;
      try {
        next = await apiRef.current.region(viewport, ac.signal);
      } catch {
        return; // aborted / network / transient — keep last good baseline
      }
      if (cancelled || mySeq !== seqRef.current) return; // superseded

      const prev = prevSnapRef.current;
      const diff = diffSnapshots(prev, next);
      prevSnapRef.current = next;

      setState((s) => {
        if (!diffHasChanges(diff)) {
          // No actionable change — only refresh the stored snapshot/region ver.
          if (s.snapshot === next) return s;
          return { ...s, snapshot: next, regionVersion: next.region_version };
        }
        const ids = [...new Set([...diff.changed, ...diff.added, ...diff.removed])].sort();
        return {
          snapshot: next,
          regionVersion: next.region_version,
          changedIdentityIds: ids,
          invalidationEpoch: s.invalidationEpoch + 1,
        };
      });
    };

    void poll();
    const t = setInterval(() => void poll(), Math.max(2_000, intervalMs));
    const onVis = () => {
      if (document.visibilityState === "visible") void poll();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, vpKey, intervalMs]);

  return state;
}
